<template>
  <ModalBase show-modal :auto-h="true" @close-modal="close" class="gk-modal-recurrent-invoice">
    <template v-slot:titlebar>
      <TitleBar variant-class="title-modal">
        <template v-slot:pretitle>
          {{ (isEdit) ? $gettext('EDIT'): $gettext('ADD') }}
        </template>
        <template v-slot:title>
          <div style="display: flex; align-items: center; gap: 1rem;">
            {{ $gettext('Schedule Invoice') }}
            <GkBadge 
              icon="fa-solid fa-crown"
              :text="$gettext('Premium')"
              variant="base"
              type="premium"
            />
          </div>
        </template>
      </TitleBar>
    </template>
    <template v-slot:maincontent>
      <b-form v-if="!loading" ref="recurrent-task-form" @submit="onSubmit">
        <!-- <p v-if="!currentCompany.is_premium" class="text-danger">
          {{ $gettext('To save scheduled invoices, you have to be a Premium user.') }}
        </p> -->
        <overlay-scrollbars :options="{ scrollbars: { autoHide: 'never' }, overflowBehavior: { y: 'scroll', x: 'hidden' } }" class="email-container">

          <div class="plan-container">
            <h4 v-translate>Plan</h4>

              <b-row>
                <b-col cols="4" style="min-width: 180px;">

                  <!-- task -->
                  <b-form-row>
                    <b-form-group id="recurrent-input-group-taskname" label-class="gk-label-margin gk-modal-label" label-for="recurrent-input-taskname">
                      <template slot="label">
                        <span v-translate>Task name</span>
                      </template>
                      <b-input-group>
                        <b-form-input
                          id="recurrent-input-taskname"
                          v-model="recurrent.name"
                          required/>
                      </b-input-group>
                    </b-form-group>
                  </b-form-row>

                  <!-- frequency -->
                  <b-form-row>
                      <b-form-group id="recurrent-input-group-interval" label-class="gk-label-margin gk-modal-label" label-for="recurrent-input-interval">
                        <template slot="label">
                          <span v-translate>Frequency</span>
                        </template>
                        <b-input-group>
                          <b-form-select id="recurrent-input-interval" v-model="recurrent.interval" :options="interval" required />
                        </b-input-group>
                      </b-form-group>
                  </b-form-row>
                  <b-form-row>
                    <b-form-group id="recurrent-input-group-frequency" label-class="gk-label-margin gk-modal-label" label-for="recurrent-input-frequency">
                      <template slot="label">
                        <span>{{ frequencyLabelText }}</span>
                      </template>

                      <b-input-group>
                        <!-- weeks -->
                        <b-form-select v-if="recurrent.interval === 'weekly'" id="recurrent-input-frequency" v-model="recurrent.skip">
                          <b-form-select-option v-for="idx in 3" :key="'weekly-' + idx" :value="idx">
                            {{ ((idx > 1) ? idx : '') }} {{ (idx > 1) ? $gettext('weeks') : $gettext('week') }}
                          </b-form-select-option>
                        </b-form-select>

                        <!-- months -->
                        <b-form-select v-if="recurrent.interval === 'monthly'" id="recurrent-input-frequency" v-model="recurrent.skip">
                          <b-form-select-option v-for="idx in 12" :key="'monthly-' + idx" :value="idx">
                            {{ ((idx > 1) ? idx : '') }} {{ (idx > 1) ? $gettext('months') : $gettext('month') }}
                          </b-form-select-option>
                        </b-form-select>

                        <!-- weekdays -->
                        <b-form-checkbox-group
                          v-if="recurrent.interval === 'daily'"
                          id="recurrent-input-frequency"
                          v-model="recurrent.days"
                          :options="daily_options"
                          name="frequency-daily"
                        />
                      </b-input-group>
                    </b-form-group>
                  </b-form-row>
                </b-col>

                <b-col cols="8">
                  <!-- start date -->
                  <b-form-group v-if="!isEdit" 
                    id="recurrent-input-group-start_date" label-class="gk-label-margin gk-modal-label" label-for="recurrent-input-start_date" 
                    style="min-width: 240px !important">
                    <template slot="label">
                      <span v-translate>Start date</span>
                    </template>
                    <b-input-group>
                      <b-form-datepicker 
                        id="recurrent-input-start_date"
                        v-model="recurrent.start_date"
                        :locale="preferredLanguage"
                      />
                    </b-input-group>
                  </b-form-group>

                  <!-- end -->
                  <b-form-group id="recurrent-input-group-ends" label-class="gk-label-margin gk-modal-label" label-for="recurrent-input-ends">
                    <template slot="label">
                      <span v-translate>Ends</span>
                    </template>

                    <b-input-group style="flex-direction: column;">
                        
                      <!-- never -->
                      <b-form-row class="mb-2">
                        <b-col cols="12">
                          <b-form-radio id="recurrent-input-end-method" v-model="recurrent.end_method" name="end-method" value="endless" class="pt-2">{{ $gettext('never') }}</b-form-radio>
                        </b-col>
                      </b-form-row>
                      
                      <!-- after -->
                      <b-form-row class="mb-2">
                        <b-col>
                          <b-form-radio v-model="recurrent.end_method" name="end-method" value="iteration" class="pt-2">{{ $gettext('after') }}</b-form-radio>
                        </b-col>
                        <b-col>
                          <b-form-row :class="{ 'opacity-25': recurrent.end_method !== 'iteration' }">
                            <b-col cols="6">
                              <b-form-input
                                id="recurrent-input-iterations"
                                v-model="recurrent.iterations"
                                :disabled="recurrent.end_method !== 'iteration'"
                                min="1" value="1" step="1"
                                type="number"
                              />
                            </b-col>
                            <b-col cols="6">
                              <label for="recurrent-input-iterations">{{ $gettext('times') }}</label>
                            </b-col>
                          </b-form-row>
                        </b-col>
                      </b-form-row>

                      <!-- on -->
                      <b-form-row>
                        <b-col>
                          <b-form-radio v-model="recurrent.end_method" name="end-method" value="date" class="pt-2">{{ $gettext('on') }}</b-form-radio>
                        </b-col>
                        <b-col :class="{ 'opacity-25': recurrent.end_method !== 'date' }">
                          <b-form-datepicker 
                            id="recurrent-input-end_date"
                            :disabled="recurrent.end_method !== 'date'"
                            v-model="recurrent.end_date"
                            :locale="preferredLanguage"
                            :min="recurrent.start_date"
                          />
                        </b-col>
                      </b-form-row>
                      
                    </b-input-group>
                  </b-form-group>
                </b-col>
              </b-row>

              <div v-if="isEdit" class="summary-box">
                <span>{{ nextDeliveryDate(recurrent) }}</span>&nbsp;<span>{{ recurrencySummary(recurrent) }}</span>
              </div>
              <div v-if="!isEdit" class="summary-box">
                <span>{{ createSummary(recurrent) }}</span>
              </div>
            </div>

            <!-- recipients -->
            <b-form-group id="recurrent-input-group-emails" label-class="gk-label-margin gk-modal-label" label-for="recurrent-input-emails">
              <template slot="label">
                <span v-translate>Recipients</span>
              </template>

              <b-input-group>
                  <b-form-input
                  id="recurrent-input-emails"
                  v-model="recurrent.emails"
                  placeholder="jane@example.com, fred@example.com"
                  required/>
              </b-input-group>
              
              <small class="form-text text-muted" style="font-size: 14px;">{{ $gettext('You can add multiple email addresses') }}</small>
              
              <b-form-checkbox
                id="checkbox-includeme"
                v-model="updateCC"
                @change="updateCCSave"
                name="checkbox-includeme">
                  <span>{{ $gettext('Send me a copy of this Invoice') }}</span>
              </b-form-checkbox>
            </b-form-group>
            
            <b-form-group
                id="input-group-subject"
                :label="$gettext('Subject*')"
                label-for="input-subject"
                label-class="gk-label-margin gk-modal-label">
              <b-form-input id="input-subject" v-model="recurrent.subject"/>
            </b-form-group>

            <!-- message -->
            <b-form-group id="recurrent-input-group-comments" label-class="gk-label-margin gk-modal-label" label-for="recurrent-input-comments">
              <template slot="label">
                <span v-translate>Email Message</span>
              </template>

              <b-input-group>
                <div class="premium-textarea-wrapper">
                  <b-form-textarea
                    id="recurrent-input-comments"
                    v-model="recurrent.comments"
                    :placeholder="$gettext('Dear ....')"
                    rows="6" no-resize/>

                    <div class="premium-info text-blue" v-if="recurrent.attach_mollie_payment">
                      <p>{{ $gettext('Pay this invoice directly using a payment link') }} <font-awesome-icon class="mx-2" role="button" @click="detachMolliePayment" icon="fa-solid fa-xmark" /></p>
                    </div>

                    <div class="premium-info text-blue" v-if="recurrent.attach_rootline_payment">
                      <p>{{ $gettext('Pay this invoice directly using a payment link') }} <font-awesome-icon class="mx-2" role="button" @click="detachRootlinePayment" icon="fa-solid fa-xmark" /></p>
                    </div>
                </div>
              </b-input-group>
              
              <PaymentLink v-model="recurrent" :currencyNotEuro="currencyNotEuro" :invoiceAmount="invoiceAmount" :fallbackAttachMollie="recurrent.attach_mollie_payment" :fallbackAttachRootline="recurrent.attach_rootline_payment" />
            </b-form-group>

            <b-form-group id="input-group-3" label-class="gk-label-margin gk-modal-label" label-for="input-3">
              <template slot="label">
                <span v-translate>Attachments</span>
              </template>
              <div class="attachments-table-wrap table-margin-mini">
                <b-table 
                  :items="fullAttachmentsList"
                  :fields="documentFields"
                  class="attachments-table"
                  :class="['gk-table gk-table--hover gk-table--responsive gk-table--headed']"
                  responsive>
                  <template #cell(title)="row">
                    <span v-if="row.item.key === 'mainpdf'">
                      {{ row.item.title }}
                      <a class="mx-2 text-blue" @click="downloadInvoice(invoice.id)" title="Download attached invoice">
                        <font-awesome-icon icon="fa-solid fa-download" />
                      </a>
                    </span>
                    <span v-else-if="row.item.key === 'hoursreport'">
                      {{ row.item.title }}
                      <a class="mx-2 text-blue" @click="downloadHoursReport" title="Download attached pdf hours report">
                        <font-awesome-icon icon="fa-solid fa-download" />
                      </a>
                    </span>
                    <span v-else>
                      {{ row.item.title }}
                    </span>
                  </template>

                  <template #cell(total)="row">
                    <span v-if="row.item.file_size">
                      {{ niceBytes(row.item.file_size, 1000, true) }}
                    </span>
                    <span v-else-if="row.item.key === 'mainpdf' || row.item.key === 'costsreport'">
                      {{ invoice.currency }} {{ row.item.total | formatNumber }}
                    </span>
                    <span v-else-if="row.item.key === 'hoursreport'">
                      {{ row.item.total }} {{ $gettext('hours') }}
                    </span>
                  </template>
                  
                  <template #cell(actions)="row">
                    <GekkoButton 
                      :disabled="row.item.key === 'mainpdf'"
                      @click="removeDocumentFromList(row)"
                      icon="fa-solid fa-trash"
                      variant="link"
                      class="text-danger"
                    />
                  </template>
                </b-table>
              </div>

              <b-form-checkbox
                id="checkbox-ubl"
                v-show="!recurrent.attach_ubl"
                v-model="recurrent.attach_ubl"
                name="checkbox-ubl">
                {{ $gettext('Send UBL with invoice') }}
                <font-awesome-icon id="tooltip-ubl" class="tooltip-icon tooltip-icon--question ml-1" icon="fa-regular fa-circle-question" />
                <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-ubl" triggers="hover" container="#report-container">
                  <div class="tooltip-text">
                    {{ $gettext('UBL (Universal Business Language) is a standard for invoices that is used across the world. It is a format used in many accountancy applications.') }}
                  </div>
                </b-tooltip>
              </b-form-checkbox>

              <div class="table-margin-mini">
                <div v-show="showDocumentSelector">
                  <DocumentSelector 
                    ref="document_selector"
                    show-file-uploader
                    @documentSelected="documentSelected"
                    @showFileUploader="displayFileUploader"
                  />
                </div>
                <GekkoButton 
                  v-if="!showDocumentSelector"
                  @click="displayDocumentSelector"
                  :text="$gettext('Add another file')"
                  icon="fa-solid fa-plus"
                  variant="link"
                  class="px-0"
                />

                <FileUploaderFull 
                  v-if="showFileUploader"
                  :document-name="$gettext('invoice') + ' ' + invoice.number"
                  @document-selected="documentSelected"/>
              </div>
            </b-form-group>
          
        </overlay-scrollbars>
      </b-form>
    </template>
    <template v-slot:actions>
      <div class="d-flex w-100 justify-content-between">
        <div>
          <GekkoButton
            v-if="!isEdit"
            :text="$gettext('Cancel')"
            variant="link"
            class="text-danger"
            @click="resetRecurrent()"
          />
          <GekkoButton
            v-else
            :text="$gettext('Close')"
            variant="light"
            @click="close()"
          />
        </div>
        <div class="d-flex" style="gap: 0.5rem;">
          <GekkoButton
            v-if="isEdit"
            :text="$gettext('Update')"
            @click="prepareAndUpdate()"
            class="position-relative"
            :loading="isWaitingForResponse"
          />
          <GekkoButton
            v-if="!isEdit"
            :text="$gettext('Save')"
            @click="prepareAndSave()"
            class="position-relative"
            :loading="isWaitingForResponse"
          />
        </div>
      </div>
    </template>
  </ModalBase>
</template>

<script>
import moment from 'moment'

import { mapGetters, mapActions, mapMutations } from "vuex";

import { promptDownload } from "@/utils/"

import recurrentInvoices from "@/mixins/recurrent.invoices";
import CompanySettingService from '@/store/services/companysetting.service.js';
import InvoiceRecurrentService from '@/store/services/invoice.recurrent.service.js';
import InvoiceService from '@/store/services/invoice.service.js';
import HourService from "@/store/services/hour.service.js";
import ModalBase from "@/components/base_components/ModalBase"
import TitleBar from "@/components/TitleBar";
import GekkoButton from "@/components/GekkoButton";
import PaymentLink from "@/components/payments/PaymentLink";
import AddProjectSimple from "@/components/contacts/AddProjectSimple"
import CustomerSelect from "@/components/invoices/ui/CustomerSelect"
import DocumentSelector from "@/components/documents/DocumentSelector";
import FileUploaderFull from "@/components/documents/FileUploaderFull";
import DocumentService from '@/store/services/document.service';
import { niceBytes } from "@/utils/";

export default {
  name: 'RecurrentInvoiceModal',
  mixins: [recurrentInvoices],
  components: { 
    ModalBase,
    TitleBar,
    GekkoButton,
    AddProjectSimple,
    CustomerSelect,
    DocumentSelector,
    FileUploaderFull,
    PaymentLink
  },
  data () {
    return {
      loading: true,
      recurrent: {},
      updateCC: false,

      amountLTEZero: false,
      currencyNotEuro: false,

      mainInvoicePDF: {
        key: 'mainpdf',
        title: '',
        total: '',
        description: ''
      },
      invoiceUBL: {
        key: 'ublfile',
        title: this.$gettext('UBL File attached'),
        description: ''
      },
      documentFields: [
        { key: "title", label: this.$gettext('Title'), class: 'tablecol-md' },
        { key: "total", label: this.$gettext('Total / Size'), class: 'tablecol-sm' },
        { key: "description", label: this.$gettext('Description'), class: 'tablecol-md' },
        { key: "actions", label: "", tdClass: "text-right" }
      ],
      showDocumentSelector: false,
      showFileUploader: false,

      selectedDocuments: [],
      paymentValid: true,
    }
  },
  computed: {
    ...mapGetters ({ 
      currentCompany: "main/getCurrentCompany",
      storeRecurrent: "invoices/getRecurrentInvoice",
      invoiceDate: "invoicesRecurrent/getInvoiceDate",
      invoice: "invoices/getInvoice",
      brandById: "brands/getBrandById",
      defaultRecurrent: "invoicesRecurrent/getDefaultRecurrent",
      recurrentBackup: "invoices/getRecurrentInvoiceBackup",
      scheduledForToday: "invoices/getScheduledForToday",
      preferredLanguage: 'main/getPreferredLanguage',
      isWaitingForResponse: 'main/isWaitingForResponse'
    }),
    isEdit () {
      return this.storeRecurrent != null
    },
    frequencyLabelText () {
      let labelText = ''
      if (this.recurrent.interval === 'daily') {
        labelText = this.$gettext('on')
      } else {
        labelText = this.$gettext('every')
      }
      return labelText
    },
    summaryLabelText () {
      let labelText = ''
      if (!this.isEdit) {
        labelText = this.$gettext('Summary')
      } else {
        labelText = this.$gettext('Next Delivery')
      }

      return labelText
    },
    fullAttachmentsList () {
      const fullList = [this.mainInvoicePDF].concat(this.selectedDocuments)

      if (this.recurrent.attach_ubl) {
        fullList.push(this.invoiceUBL)
      }

      return fullList
    },
    invoiceAmount () {
      return Math.max(parseFloat(this.invoice.total_amount), parseFloat(this.invoice.currency_total_amount))
    }
  },
  watch: {
    'recurrent.start_date' () {
      this.setNextDeliveryMsg()
    },
    'recurrent.days' () {
      if (!this.isEdit) {
        this.setNextDeliveryMsg()
      }
    },
    'recurrent.end_method' (current_end_method) {
      switch (current_end_method) {
        case 'iteration':
          this.calculateEnddateFromIteration()
          break;
        case 'date':
          this.calculateIterationFromEnddate()
          break;
      }
    },
    'recurrent.iterations' (iterations) {
      this.calculateEnddateFromIteration()
    },
  },
  created () {
    this.setupData()
  },
  mounted () {
    document.body.classList.add('modal-on')
  },
  methods: {
    ...mapMutations({
      toggleModal: 'invoicesRecurrent/TOGGLE_MODAL',
      setRecurrent: 'invoices/SET_RECURRENT_INVOICE',
      setSummary: 'invoicesRecurrent/SET_SUMMARY',
      setCompanySettings: "main/SET_CURRENT_COMPANY_SETTINGS",
      setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE'
    }),
    ...mapActions({
      saveRecurrent: 'invoicesRecurrent/saveRecurrent'
    }),
    close () {
      if (this.invoice.status === 'd') this.setSummary(null)
      document.body.classList.remove('modal-on')
      this.toggleModal()
    },
    setupData () {
      if (this.isEdit || this.recurrentBackup) {
        
        if (!this.isEdit && this.recurrentBackup) {
          this.recurrent = this.recurrentBackup
        } else {
          this.recurrent = this.storeRecurrent
        }

        // set end_date as (today + 30 days) end_date if end_method !== 'date' 
        if (this.recurrent.end_method !== 'date') {
          let end_date = moment().add(1, 'months').format('YYYY-MM-DD')
          this.recurrent.end_date = end_date
        }
        // nullify iterations if end_method !== 'iteration' 
        if (this.recurrent.end_method !== 'iteration') {
          this.recurrent.iterations = 1
        }
        // reset days to default all checked if interval !== 'daily'
        if (this.recurrent.interval !== 'daily') {
          this.recurrent.days = this.daily_options.map(option => option.value)
        } 

      } else {
        // CREATE SETUP - DEFAULT DATA
        this.recurrent = Object.assign({}, this.defaultRecurrent)
        let invoice_date = (this.invoice && this.invoice.invoice_date) ? this.invoice.invoice_date : this.invoiceDate

        // setting suggested start date as today (Feedback 17-07)
        invoice_date = new Date().format('Y-m-d')

        this.$set(this.recurrent, 'start_date', invoice_date)
        this.$set(this.recurrent, 'iterations', 1)
        this.$set(this.recurrent, 'company_profile', this.currentCompany.id)
        if (this.currentCompany.companysettings.default_invoice_email_message) {
          this.$set(this.recurrent, 'comments', this.currentCompany.companysettings.default_invoice_email_message)
        }
        if (this.invoice.recipient_customer && this.invoice.recipient_customer.email) {
          this.$set(this.recurrent, 'emails', this.invoice.recipient_customer.email)
          this.$set(this.recurrent, 'recipients', this.invoice.recipient_customer.email)
        }

        this.$set(this.recurrent, 'attach_mollie_payment', false)
        this.$set(this.recurrent, 'attach_rootline_payment', false)
        this.setNextDeliveryMsg()
      }
      // make summary available on store
      this.setSummary(this.summaryLabelText + ' ' + this.nextDeliveryDate(this.recurrent) + ' ' + this.recurrencySummary(this.recurrent))

      const invoice_amount = Math.max(parseFloat(this.invoice.total_amount), parseFloat(this.invoice.currency_total_amount))
      this.mainInvoicePDF.title = this.$gettext('Invoice #') + ' ' + this.invoice.number
      if (!this.invoice.paid_up_front) {
        this.mainInvoicePDF.description = this.$gettext('due on') + ' ' + this.invoice.due_date
      }
      if (!this.invoice.due_date) this.mainInvoicePDF.description = ''
      this.mainInvoicePDF.total = invoice_amount

      this.updateCC = this.currentCompany.companysettings.include_sender_in_cc

      let sent_from = this.currentCompany.name
      if (this.invoice.brand) {
        const invoice_brand = this.brandById(this.invoice.brand)
        if (invoice_brand && invoice_brand.name) {
          sent_from = invoice_brand.name
        }
      }
      if (!this.recurrent.subject) {
        this.$set(this.recurrent, 'subject', this.$gettext('Invoice from') + ' ' + sent_from)
      }

      if (this.recurrent.documents) {
        DocumentService.getDocumentsByIdList({id_list: `[${this.recurrent.documents}]`}).then((response) => {
          response.data.forEach((document) => {
            this.documentSelected(document)
          })
          this.loading = false
        })
      } else this.loading = false
    },
    onSubmit(event) {
      event.preventDefault()
    },
    openViewInvoice () {
      this.close()
      this.$router.push({ name: 'InvoiceViewRecurrent', params: { invoiceId: this.recurrent.id } }).catch(() => {})
    },
    setExtraData(prepared_data) {
      prepared_data.invoice = this.invoice.id
      if (this.selectedDocuments.length > 0) {
        this.$set(prepared_data, 'documents', this.selectedDocuments.map(doc => doc.id).join(','))
      }
      return prepared_data
    },
    prepareAndUpdate () {
      if (this.$refs['recurrent-task-form'].reportValidity()) {
        this.setWaitingForResponse(true)
        let update_data = this.setExtraData(this.prepareDataForSubmit(this.recurrent))
        InvoiceRecurrentService.update(update_data.id, update_data).then((response) => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Invoice Updated'),
            text: this.$gettext('Recurrent invoice updated successfully.'),
            variant: 'success',
            duration: 4
          })
          this.setSummary(this.summaryLabelText + ' ' + this.nextDeliveryDate(this.recurrent) + ' ' + this.recurrencySummary(this.recurrent))

          console.log('prepareAndUpdate setRecurrent', update_data)

          // update store data
          this.setRecurrent(this.recurrent)

          // this.recurrent = this.storeRecurrent

          this.$emit('updated-invoice')
          this.close()
        }).finally(() => {
          this.setWaitingForResponse(false)
        })
      }
    },
    prepareAndSave () {
      if (!this.currentCompany.is_premium) {
        this.close()
        this.$store.commit('togglePremiumModal', 'web_invoices_recurrent')
      } else {
        if (this.$refs['recurrent-task-form'].reportValidity() && this.invoice && this.invoice.id) {
          this.setWaitingForResponse(true)
          let prepared_recurrent = this.setExtraData(JSON.parse(JSON.stringify(this.recurrent)))

          this.setSummary(this.summaryLabelText + ' ' + this.createSummary(this.recurrent))

          this._createRecurrentTask(prepared_recurrent).then(() => {
            if (this.scheduledForToday) {
              this.$router.push({ name: 'InvoiceSendRecurrent', params: { invoiceId: this.recurrent.id } }).catch(() => {})
            } else {
              this.$router.push({ name: 'InvoiceViewRecurrent', params: { invoiceId: this.recurrent.id } }).catch(() => {})
            }
            this.close()
          }).catch((error) => {
            console.log('error', error)
          }).finally(() => {
            this.setWaitingForResponse(false)
          })

        }
      }
    },
    updateCCSave () {
      CompanySettingService.partialUpdate(this.currentCompany.companysettings.id, {
        'include_sender_in_cc': this.updateCC
      }).then((response) => {
        // update stored company settings
        this.setCompanySettings(response.data)
      })
    },
    _createRecurrentTask (recurrent_data) {
      return this.saveRecurrent(this.prepareDataForSubmit(recurrent_data)).then((recurrentResponse) => {
        this.recurrent.id = recurrentResponse.id

        // update store data
        this.setRecurrent(this.recurrent)

        this.$store.commit('invoices/REFRESH_INVOICE_RECURRENT_LIST')
        return recurrentResponse
      })
    },
    resetRecurrent () {
      this.setRecurrent(null)
      this.setSummary(null)
      this.setupData()
      this.close()
    },
    calculateEnddateFromIteration () {
      if (this.recurrent.start_date) {
        const new_date = new Date(this.recurrent.start_date)

        if (this.recurrent.interval === 'daily') {
          const days = this.recurrent.days

          let weeks = Math.floor(this.recurrent.iterations / days.length)
          if ((this.recurrent.iterations % days.length) === 0) weeks--

          const extraDays = this.recurrent.iterations - days.length * weeks

          let today_day = new Date(this.recurrent.start_date).getDay() - 1; // -1 is sunday
          if (today_day === -1) today_day = 6
          
          var c = 0, cnt = 0;
          for (var i = 0; i < 7; i++) {
            c++;
            if (days.indexOf(today_day.toString()) !== -1) {
              cnt++;
              if (cnt === extraDays) break
            }
            today_day++
            if (today_day === 7) today_day = 0
          }
          new_date.setDate(new_date.getDate() + (weeks) * 7 + c - 1)
        }
        if (this.recurrent.interval === 'weekly') {
          new_date.setDate(new_date.getDate() + this.recurrent.skip * (this.recurrent.iterations - 1) * 7)
        }
        if (this.recurrent.interval === 'monthly') {
          new_date.addMonths((this.recurrent.iterations - 1) * this.recurrent.skip)
        }
        this.recurrent.end_date = new_date.format('Y-m-d')
      }
    },
    calculateIterationFromEnddate () {
      var date_start_date = new Date(this.recurrent.start_date);
      var date_end_date = new Date(this.recurrent.end_date);
      if (this.recurrent.start_date) {
        if (date_start_date > date_end_date) {
          this.recurrent.end_date = this.recurrent.start_date;
          date_end_date = date_start_date;
        }
      }

      if (this.recurrent.interval === 'daily') {
        const weeksInBetween = Math.floor((date_end_date - date_start_date) / 1000 / 3600 / 24 + 1) / 7;
        const extraDays = Math.round((weeksInBetween - Math.trunc(weeksInBetween)) * 7);
        const nrOfWeeks = Math.trunc(weeksInBetween);
        const days = this.recurrent.days;
        this.recurrent.iterations = days.length * nrOfWeeks;

        var today_day = date_start_date.getDay() - 1; // -1 is sunday
        if (today_day === -1) today_day = 6

        var c= 0, cnt = 0;
        for (var i = 0; i < extraDays; i++) {
          if (days.indexOf(today_day.toString()) !== -1) {
            c = c + 1;
            cnt = cnt + 1;
            if (cnt === extraDays) break
          }
          today_day++;
          if (today_day === 7) today_day = 0
        }
        this.recurrent.iterations = this.recurrent.iterations + c;
      }
      if (this.recurrent.interval === 'weekly') {
        this.recurrent.iterations = date_end_date - date_start_date;
        this.recurrent.iterations = Math.floor((this.recurrent.iterations / 1000 / 3600 / 24) / 7 / this.recurrent.skip) + 1;
      }
      if (this.recurrent.interval === 'monthly') {
        this.recurrent.iterations = date_end_date - date_start_date;
        this.recurrent.iterations = Math.floor((this.recurrent.iterations / 1000 / 3600 / 24) / 30 / this.recurrent.skip) + 1;
      }
    },
    setNextDeliveryMsg () {
      let next_delivery_message = ''
      if (this.recurrent.interval === 'daily') {

        var today_day = new Date(this.recurrent.start_date).getDay() - 1; //-1 is sunday

        if (today_day == -1) {
          today_day = 6
        }
        var c = 0;
        for (var i = 0; i < 7; i++) {
          if (this.recurrent.days.indexOf(today_day.toString()) != -1) {
            break
          }
          today_day++
          if (today_day == 7) {
            today_day = 0
          }
          c++
        }

        var date_obj = new Date(this.recurrent.start_date)
        date_obj.setDate(date_obj.getDate() + c)

        this.$set(this.recurrent, 'start_date', date_obj.format('Y-m-d'))

        if (this.recurrent.start_date == Date.today().format('Y-m-d')) {
          // next_delivery_message = this.$gettext('right now')
          next_delivery_message = ''
        } else {
          next_delivery_message = this.recurrent.start_date
        }
      } else {
        if (this.recurrent.start_date == Date.today().format('Y-m-d')) {
          // next_delivery_message = this.$gettext('right now')
          next_delivery_message = ''
        } else {
          next_delivery_message = this.recurrent.start_date
        }
      }
      next_delivery_message = next_delivery_message + ' ' + this.$gettext('at 10 AM CET')
      this.$set(this.recurrent, 'next_delivery_message', next_delivery_message)
    },
    removeDocumentFromList(data) {
      switch (data.item.key) {
        case 'ublfile':
          this.$set(this.recurrent, 'attach_ubl', false)
          break;
        default:
          this.selectedDocuments = this.selectedDocuments.filter((item) => item.id != data.item.id);
          this.$refs['document_selector'].documentList.splice(0, 0, data.item);
          break;
      }
    },
    downloadInvoice(invoice_id) {
      InvoiceService.getInvoicePDFBlob(invoice_id).then(response => {
        promptDownload(response)
      }).catch(error => console.error('Error downloading PDF:', error));
    },
    downloadHoursReport () {
      let filters = {
        invoice: this.invoice.id,
        attached_to_invoice: true,
        company_profile: this.currentCompany.id,
        rounding_precision: this.currentCompany.hours_rounding_precision
      }

      HourService.getReportPDFBlob(filters).then(_ => {
        const alert_text = this.$gettext('Your download is being prepared, check your downloads page to download the file.')
        this.$store.commit('setAlert', {title: this.$gettext('Hours Report Download'), text: alert_text, variant: 'info', duration: 4})
        if (this.$route.name === "ControlPanelInvoices") {
          this.$router.push({ name: 'ControlPanelFiles' })
        } else {
          this.$router.push({ name: 'Files' })
        }
      })
      .catch(error => {
        if (error.status === 429) {
          const alert_text = this.$gettext('You\'ve reached your download limit for today.')
          this.$store.commit('setAlert', {title: this.$gettext('Hours Report Download'), text: alert_text, variant: 'danger', duration: 4})
          this.$store.commit('togglePremiumModal', 'web_files_download_limit')
        }
      })
    },
    documentSelected (item) {
      this.showDocumentSelector = false
      this.selectedDocuments.push(item)
    },
    displayFileUploader () {
      this.showFileUploader = true
      this.showDocumentSelector = false
    },
    displayDocumentSelector () {
        this.showFileUploader = false
        this.showDocumentSelector = true
    },
    niceBytes (size, base, unit = false) {
      return niceBytes(size, base, unit)
    },
    detachMolliePayment () {
      this.$set(this.recurrent, 'attach_mollie_payment', false)
    },
    detachRootlinePayment() {
      this.$set(this.recurrent, 'attach_rootline_payment', false)
    }
  }
}
</script>

<style lang="scss">
.gk-modal-recurrent-invoice {
  .gk-modal {
    max-width: 700px !important;
    .action-buttons-wrapper {
      display: flex;
      gap: 0.75rem;
      justify-content: flex-end;
    }
    .form-row { align-items: baseline; }
    // .form-group label { font-size: 1rem; }
    #recurrent-input-group-comments {
      overflow-y: auto !important;
    }
    #recurrent-input-group-interval {
      .form-group { align-items: center; }
        #recurrent-input-frequency {
        .custom-control { width: 55px; }
      }
    }
    #recurrent-input-group-ends {
      margin-bottom: 2rem;
    }
    #recurrent-input-group-summary {
      font-size: 1rem;
      padding: 1rem 0;
      border: 1px solid $colorBrand;
      border-radius: $radiusLg;
    }
  }
}

.attachments-table-wrap {
  border-radius: $radiusLg;
  box-shadow: $filtersShadow;
  overflow: hidden;
  overflow-x: auto;

  margin-bottom: 1rem;
  .attachments-table {
    background: #f4f6f5;
    thead {
      background: #fff;
      text-transform: none;
      color: $colorTextBase;
    }
    td:nth-child(1){ width: 40%; }
    td:nth-child(2){ width: 15%; }
    td:nth-child(3){ width: 40%; }
    td:nth-child(4){ width: 5%; }
  }
}

.plan-container {
  border: 1px solid $colorBorderGrey;
  border-radius: $radiusLg;
  padding: 1rem;
  margin-bottom: 1rem;
}

.email-container {
  overflow-y: auto;
  max-height: 50rem;
}

.table-margin-mini {
  width: 99%;
  margin-left: 1px;
}

.summary-box {
  font-style: italic;
  color: $colorTextSemi;
  font-size: 14px;
}
</style>